// ============================================
// SITE OF SERVICE — INTAKE FORM
// Proactive: standard clinical detail capture
//   — question bank branches by request category
//     (Imaging, Procedure, Admission, etc.)
// Salvage: sequential checkpoint narrative
//   — one question active at a time
//   — completed steps collapse to one-line summaries
//   — auto-advance with scroll
// ============================================

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import type { SiteOfServiceInputs, SoSChip, SoSVariant } from "../../types/siteOfService";
import { getSoSCompleteness } from "../../utils/siteOfServiceClassifier";
import { createEmptySoSInputs } from "../../utils/siteOfServiceGenerator";

interface SiteOfServiceInputsProps {
  inputs: SiteOfServiceInputs;
  onChange: (inputs: SiteOfServiceInputs) => void;
  defaultExpanded?: boolean;
  /** Request category from the parent form — drives which proactive questions appear */
  requestCategory?: string;
}

// ============================================
// PRIMARY DRIVER — thesis selection
// ============================================

type PrimaryDriver =
  | "condition_worsening"
  | "facility_unable"
  | "monitoring_unavailable"
  | "time_sensitive"
  | "patient_risk";

interface DriverOption {
  id: PrimaryDriver;
  label: string;
  description: string;
}

const DRIVER_OPTIONS: DriverOption[] = [
  {
    id: "condition_worsening",
    label: "Condition worsening during delay",
    description: "Patient's clinical status is deteriorating because the procedure has not occurred",
  },
  {
    id: "facility_unable",
    label: "Facility repeatedly unable to perform procedure",
    description: "Approved site has canceled or failed to deliver care one or more times",
  },
  {
    id: "monitoring_unavailable",
    label: "Required monitoring or rescue capability unavailable",
    description: "Approved site lacks postoperative monitoring or immediate intervention resources this patient needs",
  },
  {
    id: "time_sensitive",
    label: "Time-sensitive treatment window",
    description: "Clinical window for optimal outcome is narrowing or will close",
  },
  {
    id: "patient_risk",
    label: "Patient risk too high for that setting",
    description: "Patient-specific factors make the approved setting clinically unsafe",
  },
];

// ============================================
// SECTION DEFINITIONS
// ============================================

type SectionId =
  | "clinical_risk"
  | "facility_failure"
  | "delay_harm"
  | "alt_solution"
  | "monitoring"
  | "vulnerability";

interface SectionDef {
  id: SectionId;
  title: string;
  fields: FieldConfig[];
}

// ── Step type for progression ──

type ActiveStep = "driver" | SectionId | "done";

// ── Section priority map per driver ──

const DRIVER_PRIORITY: Record<PrimaryDriver, SectionId[]> = {
  condition_worsening: ["clinical_risk", "delay_harm", "vulnerability"],
  facility_unable: ["facility_failure", "delay_harm", "alt_solution"],
  monitoring_unavailable: ["monitoring", "vulnerability"],
  time_sensitive: ["clinical_risk", "delay_harm", "alt_solution"],
  patient_risk: ["vulnerability", "monitoring"],
};

// ============================================
// QUICK-PICK CHIPS — shared fields (procedure/default)
// ============================================

const RESCUE_CHIPS: SoSChip[] = [
  { label: "Immediate return-to-OR", value: "Immediate return-to-OR available" },
  { label: "Bedside intervention", value: "Bedside intervention by surgeon" },
  { label: "Surgeon on-site 24h", value: "Surgeon on-site within minutes" },
  { label: "Hourly specialized checks", value: "Hourly specialized checks by trained staff" },
];

const MON_PREF_CHIPS: SoSChip[] = [
  { label: "Hourly neuro checks", value: "Hourly neurovascular checks by trained nursing" },
  { label: "Continuous telemetry", value: "Continuous telemetry monitoring" },
  { label: "1:1 or 1:2 nursing", value: "1:1 or 1:2 nursing ratio" },
  { label: "Step-down unit", value: "Step-down/PCU level monitoring" },
];

const MON_RED_CHIPS: SoSChip[] = [
  { label: "Unknown", value: "unknown" },
  { label: "General floor care", value: "General floor-level nursing care" },
  { label: "No overnight stay", value: "No overnight observation capability" },
];

const VULN_CHIPS: SoSChip[] = [
  { label: "High BMI", value: "BMI >40 requiring specialized equipment/monitoring" },
  { label: "Cardiac risk", value: "Significant cardiac history requiring telemetry" },
  { label: "Anticoagulation", value: "Anticoagulation requiring bridging/reversal management" },
  { label: "ASA 3-4", value: "ASA class 3-4 with limited physiologic reserve" },
];

// ============================================
// QUICK-PICK CHIPS — proactive-only (procedure/default)
// ============================================

const TIMING_CHIPS: SoSChip[] = [
  { label: "Days delay", value: "Expected delay of several days" },
  { label: "Weeks delay", value: "Expected delay of 1-2+ weeks" },
  { label: "Risk during delay", value: "Clinical progression risk during delay" },
];

const COUPLING_CHIPS: SoSChip[] = [
  { label: "Combined procedures", value: "Combined procedures under single anesthetic" },
  { label: "Intra-op imaging", value: "Intraoperative imaging required" },
  { label: "Frozen section", value: "Intraoperative frozen section pathology" },
  { label: "Coordinated oncology", value: "Coordinated oncology staging" },
];

const TEAM_CHIPS: SoSChip[] = [
  { label: "ERAS protocol", value: "Enhanced recovery (ERAS) protocol specific to this site" },
  { label: "Subspecialty on-call", value: "Subspecialty backup on-call coverage" },
  { label: "Specialized nursing", value: "Specialized nursing trained in this protocol" },
];

// ============================================
// QUICK-PICK CHIPS — imaging-specific
// ============================================

const IMAGING_SEDATION_CHIPS: SoSChip[] = [
  { label: "Claustrophobia/anxiety", value: "Claustrophobia or anxiety requiring sedation" },
  { label: "Pediatric patient", value: "Pediatric patient requiring sedation for compliance" },
  { label: "Pain limits positioning", value: "Pain severity limits positioning without sedation" },
  { label: "Cognitive/developmental", value: "Cognitive or developmental needs requiring sedation" },
];

const IMAGING_SCANNER_CHIPS: SoSChip[] = [
  { label: "3T magnet required", value: "3T magnet required for diagnostic accuracy" },
  { label: "Wide-bore/open MRI", value: "Wide-bore or open MRI required for body habitus" },
  { label: "Weight capacity >350 lbs", value: "Table weight capacity >350 lbs required" },
  { label: "Specialized coils", value: "Specialized coils required for this study" },
];

const IMAGING_ALT_SITE_CHIPS: SoSChip[] = [
  { label: "Unknown", value: "unknown" },
  { label: "Standard 1.5T closed bore", value: "Standard 1.5T closed-bore MRI only" },
  { label: "No sedation capability", value: "No sedation capability at that site" },
  { label: "Limited subspecialty reads", value: "Limited subspecialty radiology reads available" },
];

const IMAGING_PATIENT_CHIPS: SoSChip[] = [
  { label: "High BMI/body habitus", value: "BMI requiring wide-bore scanner or higher weight capacity" },
  { label: "Renal insufficiency", value: "Renal insufficiency requiring on-site monitoring for contrast" },
  { label: "Severe claustrophobia", value: "Documented claustrophobia requiring sedation or open MRI" },
  { label: "Implant compatibility", value: "Implant requiring MRI-conditional scanner verification" },
];

const IMAGING_COORDINATION_CHIPS: SoSChip[] = [
  { label: "Same-day procedure", value: "Same-day procedure requiring pre-op imaging at same facility" },
  { label: "Pre-surgical planning", value: "Pre-surgical planning imaging requiring specific protocols" },
  { label: "Urgent clinical decision", value: "Urgent clinical decision pending imaging results" },
  { label: "Serial comparison", value: "Serial comparison with prior studies archived at this site" },
];

const IMAGING_RADIOLOGIST_CHIPS: SoSChip[] = [
  { label: "MSK fellowship", value: "MSK fellowship-trained radiologist for accurate read" },
  { label: "Neuroradiology", value: "Neuroradiology subspecialist required" },
  { label: "Body imaging fellowship", value: "Body imaging fellowship-trained radiologist" },
  { label: "Same radiologist", value: "Prior studies read by same radiologist for continuity" },
];

const IMAGING_OTHER_CHIPS: SoSChip[] = [
  { label: "Prior studies at this site", value: "Prior imaging archived at this facility for comparison" },
  { label: "Patient established here", value: "Patient established at this imaging center" },
  { label: "Accessibility needs", value: "Accessibility or transportation barriers to alternate site" },
];

// ============================================
// QUICK-PICK CHIPS — admission-specific
// ============================================

const ADMISSION_ACUITY_CHIPS: SoSChip[] = [
  { label: "Hemodynamic instability", value: "Hemodynamic instability requiring continuous monitoring" },
  { label: "Respiratory support", value: "Respiratory support beyond floor-level capability" },
  { label: "IV drip titration", value: "IV medication drips requiring titration" },
  { label: "Frequent neuro checks", value: "Neurological status requiring frequent reassessment" },
];

const ADMISSION_MONITORING_CHIPS: SoSChip[] = [
  { label: "Continuous telemetry", value: "Continuous telemetry monitoring" },
  { label: "ICU-level nursing", value: "ICU-level nursing ratio (1:1 or 1:2)" },
  { label: "Arterial line", value: "Arterial line monitoring" },
  { label: "Continuous pulse ox", value: "Continuous pulse oximetry with alarm" },
];

const ADMISSION_RISK_CHIPS: SoSChip[] = [
  { label: "Rapid decompensation", value: "Risk of rapid decompensation requiring immediate intervention" },
  { label: "Airway compromise", value: "Potential airway compromise requiring intubation capability" },
  { label: "Code risk", value: "Elevated cardiac arrest risk requiring code team proximity" },
  { label: "Surgical emergency", value: "Potential need for emergent surgical intervention" },
];

const ADMISSION_PATIENT_CHIPS: SoSChip[] = [
  { label: "Multiple comorbidities", value: "Multiple active comorbidities with compounding risk" },
  { label: "Immunocompromised", value: "Immunocompromised requiring isolation and close monitoring" },
  { label: "Fall risk", value: "High fall risk requiring supervised setting" },
  { label: "Altered mental status", value: "Altered mental status requiring continuous observation" },
];

// ============================================
// QUICK-PICK CHIPS — salvage
// ============================================

const ATTEMPTS_CHIPS: SoSChip[] = [
  { label: "1 time", value: "1" },
  { label: "2 times", value: "2" },
  { label: "3+ times", value: "3+" },
];

const FAILURE_REASON_CHIPS: SoSChip[] = [
  { label: "Safety hold / infection control", value: "Safety hold / infection control" },
  { label: "Unable to secure OR", value: "Unable to secure operating room time" },
  { label: "Staffing unable to support case", value: "Facility staffing unable to support case safely" },
  { label: "Equipment unavailable", value: "Required equipment unavailable at facility" },
];

const ELAPSED_CHIPS: SoSChip[] = [
  { label: "< 2 weeks", value: "Less than 2 weeks" },
  { label: "2–4 weeks", value: "2-4 weeks" },
  { label: "1–2 months", value: "1-2 months" },
  { label: "3+ months", value: "3+ months" },
];

const DETERIORATION_CHIPS: SoSChip[] = [
  { label: "Expander complications", value: "Tissue expander complications increasing" },
  { label: "Radiation window closing", value: "Post-radiation reconstruction window narrowing" },
  { label: "Disease progression risk", value: "Disease progression risk during delay" },
  { label: "Infection risk increasing", value: "Infection risk increasing with continued delay" },
  { label: "Wound deterioration", value: "Wound healing deterioration" },
  { label: "Functional decline", value: "Progressive functional and psychological decline" },
];

const ALT_FACILITY_CHIPS: SoSChip[] = [
  { label: "Immediate surgical availability", value: "Immediate surgical availability" },
  { label: "Dedicated OR block", value: "Dedicated OR block for this procedure" },
  { label: "CMS-certified, state-licensed", value: "CMS-certified and state-licensed" },
  { label: "Same surgeon and team", value: "Same surgeon, team, and protocol as approved site" },
  { label: "Lower cost to plan", value: "Lower facility fee than hospital setting" },
];

// ============================================
// FIELD CONFIG
// ============================================

interface FieldConfig {
  key: keyof SiteOfServiceInputs;
  prompt: string;
  subPrompt?: string;
  placeholder: string;
  chips: SoSChip[];
  rows?: number;
  replaceOnChip?: boolean;
}

// ── Proactive fields: PROCEDURE / DEFAULT ──

const SHARED_FIELDS: FieldConfig[] = [
  {
    key: "rescueWindow",
    prompt: "If a complication occurs in the first 24h, what can your preferred site do immediately that the alternate site cannot?",
    placeholder: "e.g., immediate return-to-OR, bedside intervention, surgeon on-site, hourly specialized checks",
    chips: RESCUE_CHIPS,
    rows: 2,
  },
  {
    key: "monitoringPreferred",
    prompt: "What is the immediate postop monitoring protocol at your site (first 24h)?",
    placeholder: "e.g., frequency, who checks, escalation pathway",
    chips: MON_PREF_CHIPS,
    rows: 2,
  },
  {
    key: "monitoringRedirected",
    prompt: "What would monitoring look like at the other facility?",
    placeholder: "If unknown, click 'Unknown' — that's a valid and useful answer",
    chips: MON_RED_CHIPS,
    rows: 2,
  },
  {
    key: "patientVulnerability",
    prompt: "What patient-specific factor makes the care environment medically important?",
    subPrompt: "If failure is missed/delayed, what irreversible outcome becomes more likely?",
    placeholder: "e.g., BMI >45 with OSA requiring continuous monitoring. If respiratory compromise is missed, irreversible hypoxic injury.",
    chips: VULN_CHIPS,
    rows: 3,
  },
];

const PROACTIVE_FIELDS: FieldConfig[] = [
  {
    key: "timingSensitivity",
    prompt: "Would moving sites change when the procedure can occur?",
    subPrompt: "If yes: estimated delay and risk during delay",
    placeholder: "e.g., 2-week delay; risk of infection progression during wait",
    chips: TIMING_CHIPS,
    rows: 2,
  },
  {
    key: "procedureCoupling",
    prompt: "Is anything coupled to this case that may require separation elsewhere?",
    placeholder: "e.g., combined procedures, immediate imaging, intra-op consult, coordinated oncology step",
    chips: COUPLING_CHIPS,
    rows: 2,
  },
  {
    key: "teamProtocol",
    prompt: "Any postoperative protocol specific to your team/site that affects safety or outcomes?",
    subPrompt: "Who would manage that protocol at the alternate site?",
    placeholder: "e.g., ERAS pathway, specialized wound care team, nursing trained in this protocol",
    chips: TEAM_CHIPS,
    rows: 2,
  },
];

// ── Proactive fields: IMAGING ──

const IMAGING_SHARED_FIELDS: FieldConfig[] = [
  {
    key: "rescueWindow",
    prompt: "Does the patient require sedation or monitoring during the study?",
    subPrompt: "If yes, what sedation or monitoring capability does your preferred site provide?",
    placeholder: "e.g., moderate sedation for claustrophobia, anesthesia support for pediatric MRI, continuous monitoring for contrast reaction risk",
    chips: IMAGING_SEDATION_CHIPS,
    rows: 2,
  },
  {
    key: "monitoringPreferred",
    prompt: "What scanner specifications does this study require?",
    subPrompt: "Magnet strength, bore size, weight capacity, specialized coils",
    placeholder: "e.g., 3T MRI for small-lesion detection, wide-bore for BMI 48, specialized breast coil",
    chips: IMAGING_SCANNER_CHIPS,
    rows: 2,
  },
  {
    key: "monitoringRedirected",
    prompt: "What is available at the insurer's preferred imaging site?",
    placeholder: "If unknown, click 'Unknown' — that's a valid and useful answer",
    chips: IMAGING_ALT_SITE_CHIPS,
    rows: 2,
  },
  {
    key: "patientVulnerability",
    prompt: "What patient-specific factors affect where this imaging can be performed?",
    subPrompt: "Body habitus, renal function, claustrophobia, implants, developmental needs",
    placeholder: "e.g., BMI 52 — standard table cannot accommodate; eGFR 35 requiring on-site monitoring during contrast administration",
    chips: IMAGING_PATIENT_CHIPS,
    rows: 3,
  },
];

const IMAGING_PROACTIVE_FIELDS: FieldConfig[] = [
  {
    key: "timingSensitivity",
    prompt: "Is this imaging coordinated with a same-day procedure, consult, or urgent clinical decision?",
    placeholder: "e.g., pre-surgical planning MRI needed before scheduled OR date; oncology staging scan with same-day biopsy",
    chips: IMAGING_COORDINATION_CHIPS,
    rows: 2,
  },
  {
    key: "procedureCoupling",
    prompt: "Does the radiologist need subspecialty expertise for this read?",
    subPrompt: "Fellowship training, prior-study continuity, complex anatomy",
    placeholder: "e.g., MSK fellowship-trained radiologist for complex spine; neuroradiologist for post-surgical brain MRI",
    chips: IMAGING_RADIOLOGIST_CHIPS,
    rows: 2,
  },
  {
    key: "teamProtocol",
    prompt: "Any other clinical reason this must be performed at your preferred site?",
    placeholder: "e.g., prior imaging archived here for serial comparison; patient has accessibility barriers to alternate site",
    chips: IMAGING_OTHER_CHIPS,
    rows: 2,
  },
];

// ── Proactive fields: ADMISSION ──

const ADMISSION_SHARED_FIELDS: FieldConfig[] = [
  {
    key: "rescueWindow",
    prompt: "What clinical acuity requires this level of care?",
    subPrompt: "What active issues would deteriorate without this care setting?",
    placeholder: "e.g., hemodynamic instability on vasopressors, respiratory failure on BiPAP, DKA requiring insulin drip titration",
    chips: ADMISSION_ACUITY_CHIPS,
    rows: 2,
  },
  {
    key: "monitoringPreferred",
    prompt: "What monitoring does this patient currently require?",
    placeholder: "e.g., continuous telemetry, arterial line, q1h neuro checks, 1:1 nursing",
    chips: ADMISSION_MONITORING_CHIPS,
    rows: 2,
  },
  {
    key: "monitoringRedirected",
    prompt: "What monitoring would be available at the lower level of care?",
    placeholder: "If unknown, click 'Unknown' — that's a valid and useful answer",
    chips: MON_RED_CHIPS,
    rows: 2,
  },
  {
    key: "patientVulnerability",
    prompt: "What patient-specific factors make downgrade or discharge unsafe?",
    subPrompt: "What irreversible outcome becomes more likely at a lower level of care?",
    placeholder: "e.g., immunocompromised post-transplant — floor nursing ratio insufficient for early sepsis detection",
    chips: ADMISSION_PATIENT_CHIPS,
    rows: 3,
  },
];

const ADMISSION_PROACTIVE_FIELDS: FieldConfig[] = [
  {
    key: "timingSensitivity",
    prompt: "What is the risk if this patient is moved to a lower level of care now?",
    subPrompt: "Specific clinical events that could occur without this care setting",
    placeholder: "e.g., rapid decompensation risk — last attempted wean from pressors failed 6 hours ago",
    chips: ADMISSION_RISK_CHIPS,
    rows: 2,
  },
  {
    key: "procedureCoupling",
    prompt: "Are any pending procedures, consults, or test results that require this setting?",
    placeholder: "e.g., pending CT angiography, IR consult for drain placement, serial troponins q6h",
    chips: [
      { label: "Pending procedure", value: "Pending procedure that requires this care level" },
      { label: "Pending consult", value: "Subspecialty consult pending that requires inpatient observation" },
      { label: "Serial labs", value: "Serial lab monitoring with clinical decision points" },
      { label: "Pending imaging", value: "Diagnostic imaging pending with potential for intervention" },
    ],
    rows: 2,
  },
  {
    key: "teamProtocol",
    prompt: "What specific interventions or protocols are currently active?",
    subPrompt: "IV drips, wound care, PT/OT frequency, nursing protocols",
    placeholder: "e.g., heparin drip with q6h PTT; wound VAC changes q48h; PT/OT BID for safe discharge",
    chips: [
      { label: "IV drip requiring titration", value: "IV medication drip requiring nurse-titrated dosing" },
      { label: "Wound care protocol", value: "Active wound care protocol requiring skilled nursing" },
      { label: "PT/OT BID+", value: "Physical/occupational therapy BID or more for safe discharge" },
      { label: "Isolation precautions", value: "Isolation precautions requiring specialized infection control" },
    ],
    rows: 2,
  },
];

// ── Salvage sections ──

const SALVAGE_SECTIONS: SectionDef[] = [
  {
    id: "clinical_risk",
    title: "Active clinical harm from continued delay",
    fields: [
      {
        key: "clinicalDeterioration",
        prompt: "What is worsening or at risk?",
        placeholder: "e.g., capsular contracture risk increasing; DIEP window narrowing",
        chips: DETERIORATION_CHIPS,
        rows: 2,
      },
    ],
  },
  {
    id: "facility_failure",
    title: "Evidence the approved facility cannot safely perform the procedure",
    fields: [
      {
        key: "priorAttempts",
        prompt: "How many times was this scheduled and could not proceed?",
        placeholder: "e.g., 3",
        chips: ATTEMPTS_CHIPS,
        rows: 1,
        replaceOnChip: true,
      },
      {
        key: "failureReasons",
        prompt: "What prevented care each time?",
        placeholder: "e.g., infection control hold × 2; OR unavailable × 1",
        chips: FAILURE_REASON_CHIPS,
        rows: 2,
      },
    ],
  },
  {
    id: "delay_harm",
    title: "Harm created by continued delay",
    fields: [
      {
        key: "elapsedDelay",
        prompt: "How long since the original surgical date?",
        placeholder: "e.g., 6 weeks",
        chips: ELAPSED_CHIPS,
        rows: 1,
        replaceOnChip: true,
      },
    ],
  },
  {
    id: "alt_solution",
    title: "Requested facility resolves the documented access failure",
    fields: [
      {
        key: "alternativeSiteCapability",
        prompt: "What does this facility offer that the approved site has not delivered?",
        placeholder: "e.g., immediate availability; same surgeon and team",
        chips: ALT_FACILITY_CHIPS,
        rows: 2,
      },
    ],
  },
  {
    id: "monitoring",
    title: "Monitoring and rescue capability at the requested facility",
    fields: [
      {
        key: "rescueWindow",
        prompt: "First-24h intervention capability",
        placeholder: "e.g., immediate return-to-OR",
        chips: RESCUE_CHIPS,
        rows: 1,
      },
      {
        key: "monitoringPreferred",
        prompt: "Postop monitoring at the requested facility",
        placeholder: "e.g., hourly neuro checks, 1:1 nursing",
        chips: MON_PREF_CHIPS,
        rows: 1,
      },
      {
        key: "monitoringRedirected",
        prompt: "Monitoring at the approved facility",
        placeholder: "If unknown, click 'Unknown'",
        chips: MON_RED_CHIPS,
        rows: 1,
      },
    ],
  },
  {
    id: "vulnerability",
    title: "Patient-specific risk factors that preclude further delay",
    fields: [
      {
        key: "patientVulnerability",
        prompt: "What makes this patient unable to safely wait?",
        placeholder: "e.g., prior radiation, limited reconstructive windows",
        chips: VULN_CHIPS,
        rows: 2,
      },
    ],
  },
];

// ============================================
// CATEGORY → FIELD CONFIG RESOLVER
// ============================================

type CategoryKey = "imaging" | "admission" | "procedure";

function resolveCategory(requestCategory?: string): CategoryKey {
  if (!requestCategory) return "procedure";
  const lower = requestCategory.toLowerCase();
  if (lower.includes("imaging")) return "imaging";
  if (lower.includes("admission")) return "admission";
  return "procedure";
}

function getSharedFieldsForCategory(cat: CategoryKey): FieldConfig[] {
  switch (cat) {
    case "imaging": return IMAGING_SHARED_FIELDS;
    case "admission": return ADMISSION_SHARED_FIELDS;
    default: return SHARED_FIELDS;
  }
}

function getProactiveFieldsForCategory(cat: CategoryKey): FieldConfig[] {
  switch (cat) {
    case "imaging": return IMAGING_PROACTIVE_FIELDS;
    case "admission": return ADMISSION_PROACTIVE_FIELDS;
    default: return PROACTIVE_FIELDS;
  }
}

// ============================================
// SECTION SUMMARY GENERATORS
// ============================================

function generateSectionSummary(
  sectionId: SectionId,
  inputs: SiteOfServiceInputs,
): string {
  const firstClause = (v: string | undefined) => {
    if (!v || v.trim().length === 0) return "";
    return v.split(/[.;]/)[0].trim();
  };
  const filled = (v: string | undefined) =>
    !!v && v.trim().length > 0 && v.trim().toLowerCase() !== "unknown";

  switch (sectionId) {
    case "clinical_risk": {
      const clause = firstClause(inputs.clinicalDeterioration);
      return clause || "Clinical risk documented";
    }
    case "facility_failure": {
      const n = parseInt(inputs.priorAttempts?.replace("+", "") || "0", 10);
      const reason = firstClause(inputs.failureReasons);
      if (n >= 1 && reason) {
        return `Procedure cancelled ${n === 1 ? "once" : n === 2 ? "twice" : `${n} times`} — ${reason.toLowerCase()}`;
      }
      if (n >= 1) {
        return `Procedure cancelled ${n === 1 ? "once" : n === 2 ? "twice" : `${n} times`} at approved facility`;
      }
      if (reason) return reason;
      return "Facility failure documented";
    }
    case "delay_harm": {
      if (filled(inputs.elapsedDelay) && filled(inputs.clinicalDeterioration)) {
        const risk = firstClause(inputs.clinicalDeterioration).toLowerCase();
        return `${inputs.elapsedDelay!.trim()} delay${risk ? ` — ${risk}` : ""}`;
      }
      if (filled(inputs.elapsedDelay)) {
        return `${inputs.elapsedDelay!.trim()} without definitive care`;
      }
      return "Delay duration documented";
    }
    case "alt_solution": {
      const cap = firstClause(inputs.alternativeSiteCapability);
      return cap ? `${cap} at requested facility` : "Alternative facility capability documented";
    }
    case "monitoring": {
      const parts: string[] = [];
      if (filled(inputs.rescueWindow)) parts.push("rescue capability");
      if (filled(inputs.monitoringPreferred)) parts.push("monitoring protocol");
      if (filled(inputs.monitoringRedirected)) parts.push("comparison");
      if (parts.length > 0) {
        return `Monitoring and rescue documented: ${parts.join(", ")}`;
      }
      return "Monitoring capability documented";
    }
    case "vulnerability": {
      const clause = firstClause(inputs.patientVulnerability);
      return clause || "Patient vulnerability documented";
    }
  }
}

// ============================================
// HELPERS
// ============================================

function sectionHasData(section: SectionDef, inputs: SiteOfServiceInputs): boolean {
  return section.fields.some((f) => {
    const v = inputs[f.key];
    return !!v && v.trim().length > 0;
  });
}

function sectionHasAnyFill(section: SectionDef, inputs: SiteOfServiceInputs): boolean {
  return section.fields.some((f) => {
    const v = inputs[f.key];
    return !!v && v.trim().length > 0 && v.trim().toLowerCase() !== "unknown" && v.trim().toLowerCase() !== "n/a";
  });
}

// ============================================
// LIVE ARGUMENT SUMMARY
// ============================================

function useLiveArgumentSummary(
  inputs: SiteOfServiceInputs,
  drivers: Set<PrimaryDriver>,
): string[] {
  return useMemo(() => {
    if (inputs.variant !== "SALVAGE") return [];

    const bullets: string[] = [];
    const isFilled = (v: string | undefined) =>
      !!v && v.trim().length > 0 && !["unknown", "n/a", "na", "not sure", ""].includes(v.trim().toLowerCase());

    if (drivers.size > 0) {
      const labels = Array.from(drivers).map(
        (d) => DRIVER_OPTIONS.find((o) => o.id === d)?.label.toLowerCase() ?? "",
      ).filter(Boolean);
      if (labels.length === 1) {
        const desc = DRIVER_OPTIONS.find((o) => o.id === Array.from(drivers)[0])?.description ?? "";
        bullets.push(desc.charAt(0).toUpperCase() + desc.slice(1));
      } else if (labels.length > 1) {
        bullets.push(`Multiple factors: ${labels.join("; ")}`);
      }
    }

    if (isFilled(inputs.clinicalDeterioration)) {
      const first = inputs.clinicalDeterioration.split(/[.;]/)[0].trim();
      if (first.length > 0) bullets.push(first.charAt(0).toUpperCase() + first.slice(1));
    }

    const attempts = parseInt(inputs.priorAttempts?.replace("+", "") || "0", 10);
    if (attempts >= 1) {
      const reason = isFilled(inputs.failureReasons)
        ? ` — ${inputs.failureReasons.split(/[;,]/)[0].trim().toLowerCase()}`
        : "";
      bullets.push(
        attempts >= 3
          ? `Approved facility unable to deliver care after ${attempts} attempts${reason}`
          : attempts === 2
          ? `Approved facility failed to deliver care twice${reason}`
          : `Approved facility unable to deliver care${reason}`,
      );
    }

    if (isFilled(inputs.elapsedDelay)) {
      bullets.push(`Patient without definitive care for ${inputs.elapsedDelay.trim().toLowerCase()}`);
    }

    if (isFilled(inputs.alternativeSiteCapability)) {
      const first = inputs.alternativeSiteCapability.split(/[;,]/)[0].trim();
      bullets.push(`Requested facility offers ${first.toLowerCase()}`);
    }

    if (isFilled(inputs.rescueWindow)) {
      const first = inputs.rescueWindow.split(/[;,]/)[0].trim();
      bullets.push(`Requested site provides ${first.toLowerCase()}`);
    } else if (isFilled(inputs.monitoringPreferred)) {
      const first = inputs.monitoringPreferred.split(/[;,]/)[0].trim();
      bullets.push(`Requested site monitoring: ${first.toLowerCase()}`);
    }

    if (isFilled(inputs.patientVulnerability)) {
      const first = inputs.patientVulnerability.split(/[.;]/)[0].trim();
      if (first.length > 0) bullets.push(`Patient factor: ${first.toLowerCase()}`);
    }

    return bullets;
  }, [inputs, drivers]);
}

// ============================================
// MAIN COMPONENT
// ============================================

export function SiteOfServiceIntake({
  inputs,
  onChange,
  defaultExpanded = false,
  requestCategory,
}: SiteOfServiceInputsProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [primaryDrivers, setPrimaryDrivers] = useState<Set<PrimaryDriver>>(new Set());
  const [activeStep, setActiveStep] = useState<ActiveStep>("driver");
  const completeness = getSoSCompleteness(inputs);
  const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const variant = inputs.variant || "PROACTIVE";
  const argumentBullets = useLiveArgumentSummary(inputs, primaryDrivers);

  // Resolve category-specific field configs
  const categoryKey = resolveCategory(requestCategory);
  const categorySharedFields = getSharedFieldsForCategory(categoryKey);
  const categoryProactiveFields = getProactiveFieldsForCategory(categoryKey);

  // Derive prioritized + supporting sections by unioning all selected drivers
  const { prioritized, supporting } = useMemo(() => {
    if (primaryDrivers.size === 0) return { prioritized: [] as SectionDef[], supporting: [] as SectionDef[] };

    // Union all priority section IDs, preserving first-seen order
    const seen = new Set<SectionId>();
    const priorityIds: SectionId[] = [];
    for (const driver of primaryDrivers) {
      for (const secId of DRIVER_PRIORITY[driver]) {
        if (!seen.has(secId)) {
          seen.add(secId);
          priorityIds.push(secId);
        }
      }
    }

    const orderedP = priorityIds
      .map((id) => SALVAGE_SECTIONS.find((s) => s.id === id))
      .filter(Boolean) as SectionDef[];

    const orderedS = SALVAGE_SECTIONS.filter((s) => !seen.has(s.id));

    return { prioritized: orderedP, supporting: orderedS };
  }, [primaryDrivers]);

  // Determine which prioritized steps are "complete" (user has advanced past them)
  const completedSteps = useMemo(() => {
    const set = new Set<ActiveStep>();
    if (primaryDrivers.size > 0 && activeStep !== "driver") {
      set.add("driver");
    }
    for (const sec of prioritized) {
      if (activeStep === sec.id) break;
      if (activeStep === "done") {
        set.add(sec.id);
        continue;
      }
      const activeIdx = prioritized.findIndex((s) => s.id === activeStep);
      const thisIdx = prioritized.findIndex((s) => s.id === sec.id);
      if (thisIdx < activeIdx) {
        set.add(sec.id);
      }
    }
    return set;
  }, [activeStep, primaryDrivers, prioritized]);

  const updateField = useCallback(
    (key: keyof SiteOfServiceInputs, value: string) => {
      onChange({ ...inputs, [key]: value });
    },
    [inputs, onChange],
  );

  const appendChip = useCallback(
    (key: keyof SiteOfServiceInputs, chipValue: string, replace?: boolean) => {
      if (replace) {
        updateField(key, chipValue);
        return;
      }
      const current = inputs[key];
      if (!current || current.trim() === "" || current.trim().toLowerCase() === "unknown") {
        updateField(key, chipValue);
      } else {
        updateField(key, `${current}; ${chipValue}`);
      }
    },
    [inputs, updateField],
  );

  const handleVariantSwitch = useCallback(
    (newVariant: SoSVariant) => {
      if (newVariant === variant) return;
      const fresh = createEmptySoSInputs(newVariant);
      fresh.rescueWindow = inputs.rescueWindow;
      fresh.monitoringPreferred = inputs.monitoringPreferred;
      fresh.monitoringRedirected = inputs.monitoringRedirected;
      fresh.patientVulnerability = inputs.patientVulnerability;
      onChange(fresh);
      setPrimaryDrivers(new Set());
      setActiveStep("driver");
    },
    [variant, inputs, onChange],
  );

  // Scroll helper
  const scrollToStep = useCallback((stepId: string) => {
    // Small delay to let React render the expanded step
    setTimeout(() => {
      const el = stepRefs.current[stepId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 80);
  }, []);

  // Toggle a driver in the multi-select set
  const handleDriverToggle = useCallback((driver: PrimaryDriver) => {
    setPrimaryDrivers((prev) => {
      const next = new Set(prev);
      if (next.has(driver)) {
        next.delete(driver);
      } else {
        next.add(driver);
      }
      return next;
    });
  }, []);

  // Continue past driver selection → advance to first prioritized section
  const handleDriverContinue = useCallback(() => {
    if (primaryDrivers.size === 0) return;

    // Compute first priority section from selected drivers
    const seen = new Set<SectionId>();
    for (const driver of primaryDrivers) {
      for (const secId of DRIVER_PRIORITY[driver]) {
        if (!seen.has(secId)) {
          setActiveStep(secId);
          scrollToStep(secId);
          return;
        }
      }
    }

    // Edge case: no prioritized sections (shouldn't happen without combination)
    setActiveStep("done");
    scrollToStep("supporting");
  }, [primaryDrivers, scrollToStep]);

  // Continue → advance to next prioritized section or done
  const handleContinue = useCallback(() => {
    const currentIdx = prioritized.findIndex((s) => s.id === activeStep);
    if (currentIdx >= 0 && currentIdx < prioritized.length - 1) {
      const nextId = prioritized[currentIdx + 1].id;
      setActiveStep(nextId);
      scrollToStep(nextId);
    } else {
      setActiveStep("done");
      scrollToStep("supporting");
    }
  }, [activeStep, prioritized, scrollToStep]);

  // Edit a completed step — re-expand it
  const handleEdit = useCallback((stepId: ActiveStep) => {
    setActiveStep(stepId);
    scrollToStep(String(stepId));
  }, [scrollToStep]);

  return (
    <div className={`rounded-xl border overflow-hidden ${
      variant === "SALVAGE"
        ? "border-slate-200 bg-white"
        : "border-amber-200/60 bg-gradient-to-r from-amber-50/50 to-orange-50/30"
    }`}>
      {/* Header — always visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full px-5 py-3.5 flex items-center justify-between transition-colors ${
          variant === "SALVAGE" ? "hover:bg-slate-50/50" : "hover:bg-amber-50/50"
        }`}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            variant === "SALVAGE" ? "bg-slate-100" : "bg-amber-100"
          }`}>
            <svg className={`w-4.5 h-4.5 ${variant === "SALVAGE" ? "text-slate-600" : "text-amber-700"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="text-left">
            <span className={`text-sm font-semibold ${variant === "SALVAGE" ? "text-slate-800" : "text-amber-900"}`}>
              {variant === "SALVAGE"
                ? "Site of Service — Approved Site Unable to Deliver Care"
                : "Site of Service — Clinical Detail"
              }
            </span>
            {!isExpanded && variant !== "SALVAGE" && (
              <span className="text-xs text-amber-600 ml-2">Strengthens your case</span>
            )}
            {!isExpanded && variant === "SALVAGE" && primaryDrivers.size > 0 && (
              <span className="text-xs text-slate-500 ml-2">
                {primaryDrivers.size === 1
                  ? DRIVER_OPTIONS.find((d) => d.id === Array.from(primaryDrivers)[0])?.label
                  : `${primaryDrivers.size} factors selected`
                }
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            completeness.filled === 0
              ? "bg-slate-100 text-slate-400"
              : completeness.filled >= (variant === "SALVAGE" ? 7 : 5)
              ? "bg-slate-700 text-white"
              : variant === "SALVAGE" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"
          }`}>
            {completeness.filled}/{completeness.total}
          </span>
          <svg
            className={`w-5 h-5 transition-transform ${isExpanded ? "rotate-180" : ""} ${
              variant === "SALVAGE" ? "text-slate-400" : "text-amber-400"
            }`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded form */}
      {isExpanded && (
        <div className={`px-5 pb-5 border-t ${
          variant === "SALVAGE" ? "border-slate-200/60" : "border-amber-200/40"
        }`}>

          {/* ── Variant toggle ── */}
          <div className="pt-3 pb-2">
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
              <button
                type="button"
                onClick={() => handleVariantSwitch("PROACTIVE")}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  variant === "PROACTIVE"
                    ? "bg-white text-slate-800 shadow-sm border border-slate-200"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Proactive
              </button>
              <button
                type="button"
                onClick={() => handleVariantSwitch("SALVAGE")}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  variant === "SALVAGE"
                    ? "bg-white text-slate-800 shadow-sm border border-slate-200"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Approved site unable to deliver care
              </button>
            </div>
          </div>

          {/* ══════════════════════════════════════════
              SALVAGE — SEQUENTIAL CHECKPOINT FLOW
              ══════════════════════════════════════════ */}
          {variant === "SALVAGE" && (
            <div className="space-y-2 pt-1">

              {/* ── LIVE ARGUMENT SUMMARY (sticky) ── */}
              {argumentBullets.length > 0 && (
                <div className="rounded-lg bg-slate-100 border border-slate-200 p-3.5 sticky top-0 z-10 mb-1">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
                    Clinical argument being built
                  </p>
                  <ul className="space-y-1">
                    {argumentBullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700 leading-snug">
                        <span className="text-slate-400 mt-0.5 flex-shrink-0">•</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ── STEP 0: PRIMARY DRIVER (multi-select) ── */}
              <div ref={(el) => { stepRefs.current["driver"] = el; }}>
                {activeStep === "driver" ? (
                  /* Active — show multi-select options */
                  <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                      <p className="text-sm font-semibold text-slate-800">
                        Why can't this patient safely receive care at the approved site?
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Select all that apply
                      </p>
                    </div>
                    <div className="p-3 space-y-1.5">
                      {DRIVER_OPTIONS.map((option) => {
                        const isSelected = primaryDrivers.has(option.id);
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => handleDriverToggle(option.id)}
                            className={`w-full text-left px-3.5 py-2.5 rounded-lg border transition-all ${
                              isSelected
                                ? "border-slate-400 bg-slate-50"
                                : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50"
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected
                                  ? "border-slate-700 bg-slate-700"
                                  : "border-slate-300 bg-white"
                              }`}>
                                {isSelected && (
                                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </span>
                              <div className="min-w-0">
                                <span className={`text-sm font-medium ${
                                  isSelected ? "text-slate-800" : "text-slate-700"
                                }`}>
                                  {option.label}
                                </span>
                                <p className={`text-xs mt-0.5 ${
                                  isSelected ? "text-slate-500" : "text-slate-400"
                                }`}>
                                  {option.description}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="px-4 pb-3.5 flex justify-end">
                      <button
                        type="button"
                        onClick={handleDriverContinue}
                        disabled={primaryDrivers.size === 0}
                        className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                          primaryDrivers.size > 0
                            ? "bg-slate-800 text-white hover:bg-slate-900 shadow-sm"
                            : "bg-slate-100 text-slate-400 cursor-not-allowed"
                        }`}
                      >
                        Continue
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Completed — one-line summary */
                  <CompletedStepRow
                    summary={
                      primaryDrivers.size === 1
                        ? DRIVER_OPTIONS.find((d) => d.id === Array.from(primaryDrivers)[0])?.label ?? ""
                        : Array.from(primaryDrivers).map((d) => DRIVER_OPTIONS.find((o) => o.id === d)?.label ?? "").join(" + ")
                    }
                    onEdit={() => handleEdit("driver")}
                  />
                )}
              </div>

              {/* ── PRIORITIZED STEPS (sequential) ── */}
              {primaryDrivers.size > 0 && prioritized.map((section, idx) => {
                const stepState: "completed" | "active" | "future" =
                  completedSteps.has(section.id) ? "completed"
                  : activeStep === section.id ? "active"
                  : "future";

                return (
                  <div
                    key={section.id}
                    ref={(el) => { stepRefs.current[section.id] = el; }}
                  >
                    {stepState === "completed" && (
                      <CompletedStepRow
                        summary={generateSectionSummary(section.id, inputs)}
                        onEdit={() => handleEdit(section.id)}
                      />
                    )}

                    {stepState === "active" && (
                      <ActiveStepCard
                        section={section}
                        inputs={inputs}
                        completeness={completeness}
                        updateField={updateField}
                        appendChip={appendChip}
                        stepNumber={idx + 2}
                        totalSteps={prioritized.length + 1}
                        onContinue={handleContinue}
                      />
                    )}

                    {stepState === "future" && (
                      <FutureStepRow title={section.title} />
                    )}
                  </div>
                );
              })}

              {/* ── SUPPORTING SECTIONS (optional, independent) ── */}
              {primaryDrivers.size > 0 && activeStep === "done" && supporting.length > 0 && (
                <div
                  className="space-y-1.5 pt-1"
                  ref={(el) => { stepRefs.current["supporting"] = el; }}
                >
                  <div className="flex items-center gap-2 py-1.5">
                    <div className="h-px flex-1 bg-slate-200/60" />
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide flex-shrink-0">
                      Add supporting detail if needed
                    </span>
                    <div className="h-px flex-1 bg-slate-200/60" />
                  </div>

                  {supporting.map((section) => (
                    <SupportingSection
                      key={section.id}
                      section={section}
                      inputs={inputs}
                      completeness={completeness}
                      updateField={updateField}
                      appendChip={appendChip}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════
              PROACTIVE — category-aware question bank
              ══════════════════════════════════════════ */}
          {variant === "PROACTIVE" && (
            <div className="space-y-4 pt-1">
              <p className="text-xs text-slate-500">
                All fields optional — even partial answers strengthen the case. Click chips to quick-fill.
              </p>
              {categorySharedFields.map((field) => (
                <SoSField
                  key={field.key}
                  config={field}
                  value={inputs[field.key] ?? ""}
                  isFilled={!!completeness.fields[field.key]}
                  onChangeValue={(val) => updateField(field.key, val)}
                  onChipClick={(chipVal) => appendChip(field.key, chipVal)}
                />
              ))}
              {categoryProactiveFields.map((field) => (
                <SoSField
                  key={field.key}
                  config={field}
                  value={inputs[field.key] ?? ""}
                  isFilled={!!completeness.fields[field.key]}
                  onChangeValue={(val) => updateField(field.key, val)}
                  onChipClick={(chipVal) => appendChip(field.key, chipVal)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// COMPLETED STEP — one-line summary + edit
// ============================================

function CompletedStepRow({
  summary,
  onEdit,
}: {
  summary: string;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-700 flex items-center justify-center">
        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </span>
      <p className="text-sm text-slate-700 flex-1 min-w-0 truncate">{summary}</p>
      <button
        type="button"
        onClick={onEdit}
        className="flex-shrink-0 p-1 rounded hover:bg-slate-100 transition-colors group"
        title="Edit this step"
      >
        <svg className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      </button>
    </div>
  );
}

// ============================================
// ACTIVE STEP — full form card
// ============================================

interface ActiveStepCardProps {
  section: SectionDef;
  inputs: SiteOfServiceInputs;
  completeness: ReturnType<typeof getSoSCompleteness>;
  updateField: (key: keyof SiteOfServiceInputs, value: string) => void;
  appendChip: (key: keyof SiteOfServiceInputs, chipValue: string, replace?: boolean) => void;
  stepNumber: number;
  totalSteps: number;
  onContinue: () => void;
}

function ActiveStepCard({
  section,
  inputs,
  completeness,
  updateField,
  appendChip,
  stepNumber,
  totalSteps,
  onContinue,
}: ActiveStepCardProps) {
  const hasAnyData = sectionHasAnyFill(section, inputs);

  return (
    <div className="rounded-lg border border-slate-300 bg-white shadow-sm overflow-hidden">
      {/* Step header */}
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-800 text-white text-[10px] font-bold">
            {stepNumber}
          </span>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
            {section.title}
          </p>
        </div>
        <span className="text-[10px] text-slate-400">
          {stepNumber} of {totalSteps}
        </span>
      </div>

      {/* Fields */}
      <div className="p-4 space-y-3">
        {section.fields.map((field) => (
          <SoSField
            key={field.key}
            config={field}
            value={inputs[field.key] ?? ""}
            isFilled={!!completeness.fields[field.key]}
            onChangeValue={(val) => updateField(field.key, val)}
            onChipClick={(chipVal) => appendChip(field.key, chipVal, field.replaceOnChip)}
          />
        ))}
      </div>

      {/* Continue button */}
      <div className="px-4 pb-3.5 flex justify-end">
        <button
          type="button"
          onClick={onContinue}
          className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
            hasAnyData
              ? "bg-slate-800 text-white hover:bg-slate-900 shadow-sm"
              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          Continue
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ============================================
// FUTURE STEP — muted placeholder
// ============================================

function FutureStepRow({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-dashed border-slate-200/60">
      <span className="flex-shrink-0 w-4.5 h-4.5 rounded-full bg-slate-100 border border-slate-200" />
      <p className="text-xs text-slate-400 truncate">{title}</p>
    </div>
  );
}

// ============================================
// SUPPORTING SECTION — independent collapsible
// ============================================

function SupportingSection({
  section,
  inputs,
  completeness,
  updateField,
  appendChip,
}: {
  section: SectionDef;
  inputs: SiteOfServiceInputs;
  completeness: ReturnType<typeof getSoSCompleteness>;
  updateField: (key: keyof SiteOfServiceInputs, value: string) => void;
  appendChip: (key: keyof SiteOfServiceInputs, chipValue: string, replace?: boolean) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const hasData = sectionHasData(section, inputs);

  return (
    <div className={`rounded-lg border transition-colors ${
      isOpen ? "border-slate-200 bg-white/80" : "border-slate-100 bg-slate-50/30"
    }`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {hasData && !isOpen ? (
            <span className="flex-shrink-0 w-4 h-4 rounded-full bg-slate-600 flex items-center justify-center">
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </span>
          ) : (
            <span className="flex-shrink-0 w-4 h-4 rounded-full bg-slate-100 border border-slate-200" />
          )}
          <p className={`text-xs font-medium truncate ${
            hasData && !isOpen ? "text-slate-700" : isOpen ? "text-slate-700" : "text-slate-400"
          }`}>
            {hasData && !isOpen
              ? generateSectionSummary(section.id, inputs)
              : section.title
            }
          </p>
          {!isOpen && !hasData && (
            <span className="text-[10px] text-slate-300 flex-shrink-0">optional</span>
          )}
        </div>
        <svg
          className={`w-3.5 h-3.5 text-slate-300 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="px-3.5 pb-3 space-y-3 border-t border-slate-100">
          {section.fields.map((field) => (
            <SoSField
              key={field.key}
              config={field}
              value={inputs[field.key] ?? ""}
              isFilled={!!completeness.fields[field.key]}
              onChangeValue={(val) => updateField(field.key, val)}
              onChipClick={(chipVal) => appendChip(field.key, chipVal, field.replaceOnChip)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// INDIVIDUAL FIELD
// ============================================

interface SoSFieldProps {
  config: FieldConfig;
  value: string;
  isFilled: boolean;
  onChangeValue: (val: string) => void;
  onChipClick: (chipVal: string) => void;
}

function SoSField({ config, value, isFilled, onChangeValue, onChipClick }: SoSFieldProps) {
  return (
    <div className="space-y-1.5 pt-1">
      <div className="flex items-start justify-between gap-2">
        <label className="text-sm font-medium leading-snug text-slate-700">
          {config.prompt}
        </label>
        {isFilled && (
          <span className="flex-shrink-0 w-4 h-4 rounded-full bg-slate-200 flex items-center justify-center mt-0.5">
            <svg className="w-2.5 h-2.5 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </span>
        )}
      </div>

      {config.subPrompt && (
        <p className="text-xs -mt-0.5 text-slate-500">
          {config.subPrompt}
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {config.chips.map((chip) => (
          <button
            key={chip.value}
            type="button"
            onClick={() => onChipClick(chip.value)}
            className="px-2.5 py-1 text-xs rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
          >
            {chip.label}
          </button>
        ))}
      </div>

      <textarea
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        placeholder={config.placeholder}
        rows={config.rows || 2}
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 bg-white resize-none transition-colors focus:border-slate-400 focus:ring-1 focus:ring-slate-200"
      />
    </div>
  );
}